<template>
  <div id="selected-product-itemized-pop-up" class="selected-product-itemized-pop-up">
    <div class="overlay-div" @click.stop="handleClose"></div>
    <div
      class="default-pop-up-center-div center bordered"
      :class="{ blur: isBlurred }"
    >
      <div class="selected-product-itemized-pop-up__container">
        <!-- Header -->
        <div class="selected-product-itemized-pop-up__header">
          <div>
            <span class="h1">{{ productName }}</span>
          </div>
          <div class="selected-product-itemized-pop-up__header-subtitle text-color-1">
            <div>{{ getGroup }}</div>
            <div v-if="getSupplier">-</div>
            <div v-if="getSupplier">{{ getSupplier }}</div>
            <div v-if="isCannabis">-</div>
            <div v-if="isCannabis">{{ cannabisGrams }}g cannabis</div>
          </div>
        </div>

        <!-- Warning Banner -->
        <div class="warning-banner">
          <i class="fas fa-exclamation-triangle"></i>
          <span>Changing quantities may affect applied discounts</span>
        </div>

        <!-- Items List -->
        <div class="items-list">
          <div class="items-list__title">
            <span class="text-color-1">ITEMS IN CART</span>
          </div>

          <div
            v-for="(item, index) in productItems"
            :key="item._id || item.itemId || index"
            class="item-card elevated-1"
          >
            <div class="item-card__header">
              <div class="item-card__batch">
                <span class="text-color-1">Batch</span>
                <span>#{{ truncateBatchId(item.batchId) }}</span>
              </div>
              <div class="item-card__quantity">
                <span class="text-color-1">Qty:</span>
                <span>{{ item.quantity }}</span>
              </div>
            </div>

            <div class="item-card__price">
              <span :class="{ discounted: isDiscounted(item) }">
                {{ formatCentsToPrice(item.originalUnitPrice) }} each
              </span>
              <span v-if="isDiscounted(item)" class="price-discounted">
                → {{ formatCentsToPrice(item.discountedUnitPrice) }}
              </span>
            </div>

            <!-- Discounts Applied -->
            <div v-if="item.discountsApplied && item.discountsApplied.filter(d => d.effectType !== 'QUALIFYING_ITEM').length > 0" class="item-card__discounts">
              <div
                v-for="(discount, dIndex) in item.discountsApplied.filter(d => d.effectType !== 'QUALIFYING_ITEM')"
                :key="'discount-' + dIndex"
                class="discount-row"
              >
                <span class="discount-icon"><i class="fas fa-tag"></i></span>
                <span class="discount-name">{{ discount.ruleName }}</span>
                <span class="discount-effect">{{ formatEffectType(discount.effectType, discount.effectValue) }}</span>
                <span class="discount-amount">-{{ formatCentsToPrice(discount.totalDiscount) }}</span>
              </div>
            </div>

            <!-- Item Controls -->
            <div class="item-card__controls">
              <BaseBtn
                :disabled="item.quantity <= 1 || isLoading"
                iconGroup="fas"
                iconName="minus"
                @click="decreaseItemQuantity(item)"
                :colorType="1"
                size="sm"
              />
              <span class="item-quantity-display">{{ item.quantity }}</span>
              <BaseBtn
                :disabled="isLoading"
                iconGroup="fas"
                iconName="plus"
                @click="increaseItemQuantity(item)"
                size="sm"
              />
              <BaseBtn
                :disabled="isLoading"
                label="Remove"
                @click="confirmRemoveItem(item)"
                :colorType="1"
                size="sm"
                class="remove-item-btn"
              />
            </div>
          </div>

          <div v-if="productItems.length === 0" class="no-items">
            No items in cart for this product
          </div>
        </div>

        <!-- Footer Summary -->
        <div class="summary-footer">
          <div class="summary-row">
            <span>Total: {{ totalItemCount }} item{{ totalItemCount !== 1 ? 's' : '' }}</span>
            <span>Subtotal: {{ formatCentsToPrice(totalSubtotal) }}</span>
          </div>
          <div v-if="totalDiscount > 0" class="summary-row discount-summary">
            <span>Total Discount:</span>
            <span class="discount-amount">-{{ formatCentsToPrice(totalDiscount) }}</span>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="padded button-wrapper">
        <BaseBtn
          :disabled="productItems.length === 0 || isLoading"
          label="Remove All"
          @click="confirmRemoveAll"
          :colorType="1"
        />
        <BaseBtn
          @click="handleClose"
          label="Close"
          :colorType="3"
          :disabled="isLoading"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import BaseBtn from "@/components/Base/Button/Button.vue";

export default {
  name: "SelectedProductItemizedPopUp",
  data() {
    return {
      isBlurred: false,
    };
  },
  components: {
    BaseBtn,
  },
  created() {
    window.addEventListener("keyup", this.handleKeyUp);
  },
  beforeDestroy() {
    window.removeEventListener("keyup", this.handleKeyUp);
  },
  computed: {
    ...mapGetters({
      selectedProductItemizedPopUp: "posModule/selectedProductItemizedPopUp",
      orderLoadingStatus: "orderModule/loadingStatus",
      order: "orderModule/posOrder",
    }),
    product() {
      return this.selectedProductItemizedPopUp?.product || {};
    },
    productName() {
      return this.product?.name || "";
    },
    getGroup() {
      return this.product?.externalFields?.group?.name || "";
    },
    getSupplier() {
      return this.product?.externalFields?.supplier?.name || "";
    },
    isCannabis() {
      return this.product?.externalFields?.group?.isCannabis || false;
    },
    cannabisGrams() {
      return this.product?.cannabisGramsUnit || 0;
    },
    isLoading() {
      return this.orderLoadingStatus?.addRemoveItemToOrder || false;
    },
    productItems() {
      if (!this.order?.itemsMeta || !this.product?._id) return [];
      const productId = String(this.product._id);
      return this.order.itemsMeta.filter(
        (item) => String(item.productId) === productId
      );
    },
    totalItemCount() {
      return this.productItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
    },
    totalSubtotal() {
      return this.productItems.reduce((sum, item) => sum + (item.discountedSubTotal || 0), 0);
    },
    totalDiscount() {
      return this.productItems.reduce((sum, item) => sum + (item.totalDiscount || 0), 0);
    },
  },
  methods: {
    ...mapActions({
      addRemoveItemToOrder: "orderModule/addRemoveItemToOrder",
      setSelectedProductItemizedPopUp: "posModule/setSelectedProductItemizedPopUp",
      setMessagePopUp: "setMessagePopUp",
    }),
    handleKeyUp(e) {
      if (e.keyCode === 27) {
        this.handleClose();
      }
    },
    formatCentsToPrice(cents) {
      if (cents === undefined || cents === null) return "$0.00";
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(cents / 100);
    },
    truncateBatchId(batchId) {
      if (!batchId) return "-";
      if (batchId.length > 16) {
        return batchId.slice(0, 8) + "..." + batchId.slice(-4);
      }
      return batchId;
    },
    isDiscounted(item) {
      return item.totalDiscount > 0 || item.originalUnitPrice > item.discountedUnitPrice;
    },
    formatEffectType(effectType, effectValue) {
      if (!effectType) return "";
      switch (effectType) {
        case "PERCENT_OFF":
          return `${effectValue}% off`;
        case "DOLLAR_OFF":
          return `$${(effectValue / 100).toFixed(2)} off`;
        case "FIXED_PRICE":
          return `Fixed $${(effectValue / 100).toFixed(2)}`;
        case "FREE":
          return "Free";
        default:
          return effectType;
      }
    },
    handleClose() {
      if (this.selectedProductItemizedPopUp?.negativeCallback) {
        this.selectedProductItemizedPopUp.negativeCallback();
      }
      this.setSelectedProductItemizedPopUp({ isOpen: false });
    },
    increaseItemQuantity(item) {
      this.addRemoveItemToOrder({
        action: {
          productId: this.product._id,
          mode: "add",
          quantity: 1,
          forcedItem: { _id: item.itemId, batchId: item.batchId },
        },
        positiveCallback: () => {
          if (this.selectedProductItemizedPopUp?.positiveCallback) {
            this.selectedProductItemizedPopUp.positiveCallback();
          }
        },
      });
    },
    decreaseItemQuantity(item) {
      if (item.quantity <= 1) {
        this.confirmRemoveItem(item);
        return;
      }
      this.addRemoveItemToOrder({
        action: {
          productId: this.product._id,
          mode: "subtract",
          quantity: 1,
          forcedItem: { _id: item.itemId, batchId: item.batchId },
        },
        positiveCallback: () => {
          if (this.selectedProductItemizedPopUp?.positiveCallback) {
            this.selectedProductItemizedPopUp.positiveCallback();
          }
        },
      });
    },
    confirmRemoveItem(item) {
      this.isBlurred = true;
      this.setMessagePopUp({
        isOpen: true,
        title: "Warning!",
        message: "Do you want to remove this item from the cart? Applied discounts may be recalculated.",
        positiveCallback: () => {
          this.performRemoveItem(item);
        },
        negativeCallback: () => {
          this.isBlurred = false;
        },
      });
    },
    performRemoveItem(item) {
      this.isBlurred = false;
      this.addRemoveItemToOrder({
        action: {
          productId: this.product._id,
          mode: "subtract",
          quantity: item.quantity,
          forcedItem: { _id: item.itemId, batchId: item.batchId },
        },
        positiveCallback: () => {
          if (this.selectedProductItemizedPopUp?.positiveCallback) {
            this.selectedProductItemizedPopUp.positiveCallback();
          }
          // Close if no items left
          if (this.productItems.length <= 1) {
            this.setSelectedProductItemizedPopUp({ isOpen: false });
          }
        },
      });
    },
    confirmRemoveAll() {
      this.isBlurred = true;
      this.setMessagePopUp({
        isOpen: true,
        title: "Warning!",
        message: "Do you want to remove all items of this product from the cart? Applied discounts will be recalculated.",
        positiveCallback: this.performRemoveAll,
        negativeCallback: () => {
          this.isBlurred = false;
        },
      });
    },
    performRemoveAll() {
      this.isBlurred = false;
      this.addRemoveItemToOrder({
        action: {
          productId: this.product._id,
          mode: "remove",
        },
        positiveCallback: () => {
          if (this.selectedProductItemizedPopUp?.positiveCallback) {
            this.selectedProductItemizedPopUp.positiveCallback();
          }
          this.setSelectedProductItemizedPopUp({ isOpen: false });
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.selected-product-itemized-pop-up {
  &__container {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    max-height: 70vh;
    overflow-y: auto;
  }

  &__header {
    padding-bottom: 5px;
    width: 100%;
    text-align: center;
  }

  &__header-subtitle {
    display: flex;
    justify-content: center;
    font-weight: bold;
    font-size: $text-size-2;
    gap: 5px;
  }
}

.warning-banner {
  background-color: rgba(243, 156, 18, 0.15);
  border: 1px solid #f39c12;
  border-radius: 6px;
  padding: 10px 15px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: #f39c12;
  font-size: $text-size-2;

  i {
    font-size: 1.1em;
  }
}

.items-list {
  display: flex;
  flex-direction: column;
  gap: 10px;

  &__title {
    font-weight: bold;
    font-size: $text-size-2;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
}

.item-card {
  border-radius: 8px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__batch {
    display: flex;
    gap: 5px;
    font-size: $text-size-2;
  }

  &__quantity {
    display: flex;
    gap: 5px;
    font-weight: bold;
  }

  &__price {
    display: flex;
    gap: 10px;
    align-items: center;

    .discounted {
      text-decoration: line-through;
      opacity: 0.6;
    }

    .price-discounted {
      color: #27ae60;
      font-weight: bold;
    }
  }

  &__discounts {
    border-top: 1px dashed rgba(255, 255, 255, 0.1);
    padding-top: 8px;
    margin-top: 4px;
  }

  &__controls {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);

    .item-quantity-display {
      min-width: 30px;
      text-align: center;
      font-weight: bold;
      font-size: 1.1em;
    }

    .remove-item-btn {
      margin-left: auto;
    }
  }
}

.light .item-card {
  &__discounts {
    border-top-color: rgba(0, 0, 0, 0.1);
  }

  &__controls {
    border-top-color: rgba(0, 0, 0, 0.1);
  }
}

.discount-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  font-size: 0.9em;

  .discount-icon {
    color: #f39c12;
  }

  .discount-name {
    flex: 1;
    color: #f39c12;
  }

  .discount-effect {
    color: $dark-text-color-1;
    font-style: italic;
  }

  .discount-amount {
    color: #27ae60;
    font-weight: 600;
  }
}

.light .discount-row {
  .discount-effect {
    color: $light-text-color-1;
  }
}

.no-items {
  text-align: center;
  padding: 20px;
  color: $dark-text-color-1;
  font-style: italic;
}

.light .no-items {
  color: $light-text-color-1;
}

.summary-footer {
  border-top: 2px solid rgba(255, 255, 255, 0.2);
  padding-top: 12px;
  margin-top: 8px;

  .summary-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-weight: bold;

    &.discount-summary {
      color: #f39c12;
      margin-top: 5px;
    }

    .discount-amount {
      color: #27ae60;
    }
  }
}

.light .summary-footer {
  border-top-color: rgba(0, 0, 0, 0.2);
}

.button-wrapper {
  display: flex;
  gap: 10px;
  width: 100%;

  & > button {
    flex: 1;
  }
}
</style>

